import { useEffect, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { useMyServices, serviceProviderScope } from '@/api/services';
import {
  findCatalogProduct,
  serviceRoute,
  categoryBackTarget,
  type ProductStatus,
} from './categories';
import { trackCatalogView } from '@/lib/analytics-events';
import { CatalogProductLanding } from './CatalogProductLanding';
import { ComputeActivateLanding } from './compute/ComputeActivateLanding';
import { KubernetesActivateLanding } from './kubernetes/KubernetesActivateLanding';
import { StreamingActivateLanding } from './streaming/StreamingActivateLanding';
import { StorageLanding } from './oss/StorageLanding';
import { CdnActivateLanding } from './cdn/CdnActivateLanding';
import { waapConsoleRoute } from './waap/waap-service';

const STATUS_META: Record<ProductStatus, { tone: 'success' | 'info' | 'warning'; label: string }> = {
  active: { tone: 'success', label: 'Available' },
  beta: { tone: 'info', label: 'Beta' },
  coming_soon: { tone: 'warning', label: 'Coming soon' },
};

/**
 * Per-product catalog landing reached from the services dashboard product cards.
 * Shows the product overview + features and a path to get it. If the customer
 * already has the service provisioned, jump straight into its console.
 *
 * URL is `/services/catalog/:category/:product` (dot-free segments) — see
 * `catalogPath` in ./categories for why scopes can't be used in the path.
 */
export default function ServiceCatalogDetail() {
  const { category: categoryKey, product: productName } = useParams<{
    category: string;
    product: string;
  }>();
  const navigate = useNavigate();
  const { data: services = [], isLoading } = useMyServices();

  const match = useMemo(
    () => findCatalogProduct(categoryKey, productName),
    [categoryKey, productName],
  );
  const existing = useMemo(() => {
    if (!match) return undefined;
    // Self-serve products provision on a specific provider (Multi-CDN is
    // catalogued as owl/External.Mlytics but activates on raven/vEdge), and the
    // backend gates one service per (org, provider). Detect an existing
    // activation by that provider — matching the catalog scope would miss the
    // raven service and offer a second activation that 409s.
    if (match.product.activateProvider) {
      return services.find(
        (s) => s.provider?.name?.trim().toLowerCase() === match.product.activateProvider,
      );
    }
    return services.find((s) => serviceProviderScope(s) === match.product.scope);
  }, [services, match]);

  // Interest signal: which catalog products customers view. Fires once per product.
  useEffect(() => {
    if (match) {
      trackCatalogView(match.product.displayName, match.product.scope, match.category.title, match.product.status);
    }
  }, [match?.product.scope]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <Container size="lg">
        <Skeleton style={{ height: 72, marginBottom: 'var(--vn-space-5)' }} />
        <Skeleton style={{ height: 220 }} />
      </Container>
    );
  }

  // Already provisioned → open the console (or the dashboard when not yet wired).
  // Route by the existing service's OWN scope: a self-serve product's catalog
  // scope (owl → /services/waf) differs from where the activated provider lives
  // (raven → /services/cdn).
  if (existing && match) {
    // WAAP is keyed off its provider (cobra), not a scope serviceRoute knows —
    // send it to its own console explicitly.
    const to =
      match.product.scope === 'Security.WAAP'
        ? waapConsoleRoute(existing.id)
        : serviceRoute(serviceProviderScope(existing), existing.id) ?? '/services';
    return <Navigate to={to} replace />;
  }

  if (!match) {
    return (
      <Container size="lg">
        <EmptyState
          icon={<ArrowLeft size={26} />}
          title="Service not found"
          description="The requested service is not in the catalog."
          actions={
            <Button variant="secondary" onClick={() => navigate('/services')}>
              Back to services
            </Button>
          }
        />
      </Container>
    );
  }

  const { product, category } = match;
  const Icon = category.icon;
  const meta = STATUS_META[product.status];
  const coming = product.status === 'coming_soon';
  const back = () => navigate(categoryBackTarget(category.key).path);

  // Self-serve products share ONE activation surface with their service console:
  // the product's own activation landing. Each landing implements the single flow
  // — staff impersonation provisions in-app, a customer sends a contact-sales
  // inquiry — plus any prerequisite (Managed Kubernetes requires an active Cloud
  // Compute first). Non-self-serve products fall through to the catalog preview +
  // contact/waitlist inquiry below.
  if (!coming && product.activateProvider) {
    switch (product.activateProvider) {
      case 'panda':
        return <ComputeActivateLanding onBack={back} />;
      case 'wolf':
        return <KubernetesActivateLanding onBack={back} />;
      case 'penguin':
        return <StreamingActivateLanding provider="penguin" onBack={back} />;
      case 'lens':
        return <StreamingActivateLanding provider="lens" onBack={back} />;
      case 'raven':
        return <CdnActivateLanding onBack={back} />;
      case 'cobra':
        return <Navigate to="/services/waap" replace />;
    }
  }

  // Object Storage is self-serve through its own landing but isn't keyed by an
  // activateProvider in the catalog — dispatch it to that landing by scope so it
  // matches the other heavy services' one activation surface.
  if (!coming && product.scope === 'Internal.Minio') {
    return <StorageLanding onBack={back} />;
  }

  // Every other product (no dedicated landing) shares the generic hero landing so
  // the whole catalog looks the same. Its CTA follows the single gate: staff
  // impersonation self-activates a provisionable product, otherwise it opens a
  // contact-sales / waitlist inquiry (the only path for these no-provider products).
  return (
    <CatalogProductLanding
      product={product}
      Icon={Icon}
      statusLabel={meta.label}
      statusTone={meta.tone}
      onBack={back}
    />
  );
}
